import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { ArrowRight, ArrowLeft, Copy, CheckCircle2 } from 'lucide-react';

export function DuplicateStep({ duplicateCount, duplicateAction, onDuplicateActionChange, onBack, onNext }) {
  if (duplicateCount === 0) {
    return (
      <div className="space-y-6" data-testid="duplicate-step">
        <Card>
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
            <p className="text-sm font-medium">No rows matched an existing product</p>
            <p className="text-xs text-muted-foreground mt-1">Valid rows will be added as new products. Rows with errors will not be imported.</p>
          </CardContent>
        </Card>
        <div className="flex justify-between">
          <Button variant="outline" className="gap-1.5" onClick={onBack} data-testid="duplicate-back-btn">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <Button className="gap-1.5" onClick={onNext} data-testid="duplicate-next-btn">
            Import Products <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="duplicate-step">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Copy className="h-4 w-4 text-primary" /> {duplicateCount} duplicate row{duplicateCount > 1 ? 's' : ''} found
          </CardTitle>
          <CardDescription data-testid="duplicate-matching-policy">Matched by exact name or barcode. Repeated rows within this file are always skipped; conflicting matches must be corrected.</CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup value={duplicateAction} onValueChange={onDuplicateActionChange} className="space-y-3" data-testid="duplicate-action-group">
            <label className="flex items-start gap-3 p-3 rounded-lg border border-border/60 cursor-pointer hover:border-primary/40" htmlFor="dup-skip">
              <RadioGroupItem value="skip" id="dup-skip" className="mt-0.5" data-testid="duplicate-action-skip" />
              <div>
                <p className="text-sm font-medium">Skip duplicates and report them</p>
                <p className="text-xs text-muted-foreground">The matching products are left exactly as they are. Skipped rows are listed in the results.</p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-lg border border-border/60 cursor-pointer hover:border-primary/40" htmlFor="dup-update">
              <RadioGroupItem value="update" id="dup-update" className="mt-0.5" data-testid="duplicate-action-update" />
              <div>
                <p className="text-sm font-medium">Update the matching products with this file's values</p>
                <p className="text-xs text-muted-foreground">Non-blank mapped values replace existing values; existing product names are kept. An opening batch adds stock; a plain stock column replaces it.</p>
              </div>
            </label>
          </RadioGroup>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" className="gap-1.5" onClick={onBack} data-testid="duplicate-back-btn">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button className="gap-1.5" onClick={onNext} data-testid="duplicate-next-btn">
          Import Products <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
